import {
  Alert,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import { useFlags } from "launchdarkly-react-client-sdk";
import { FC, useEffect } from "react";
import { useOrganization } from "../../pages/facility/hooks/useOrganization";
import { EncounterMode, EncounterType } from "../../types/medicalEncounter";
import useEntitlements from "../../hooks/useEntitlements";

interface EncounterModeSelectorProps {
  value: EncounterType;
  onChange: (value: EncounterType) => void;
  showAlert?: boolean;
  alertMessage?: string;
  disabled?: boolean;
  showPaperOption?: boolean;
  showSimpleOption?: boolean;
  showFullOption?: boolean;
  label?: string;
}

const EncounterModeSelector: FC<EncounterModeSelectorProps> = ({
  value,
  onChange,
  showAlert = true,
  alertMessage = 'Detailed encounters are disabled in our Essentials plan. Please upgrade to enable "Detailed" notes.',
  disabled = false,
  showPaperOption = true,
  showSimpleOption = true,
  showFullOption = true,
  label = "",
}) => {

  const {
    allowSimplifiedEncounter,
    allowFullEncounter,
    allowHistoricalEncounterUpload,
  } = useFlags();
  const { can } = useEntitlements();

  const isDetailedRestricted = !can("feature_detailed_encounters");

  // Handle the restricted logic - if restricted, default to simple and disable full
  useEffect(() => {
    if (isDetailedRestricted && value === "full") {
      onChange("simple");
    }
  }, [isDetailedRestricted, value, onChange]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value as EncounterType;
    if (!["simple", "full", "paper"].includes(newValue)) {
      return;
    }
    onChange(newValue);
  };

  return (
    <FormControl disabled={disabled}>
      {label && <FormLabel>{label}</FormLabel>}
      {isDetailedRestricted && showAlert && <Alert severity="info">{alertMessage}</Alert>}
      <RadioGroup row value={value} onChange={handleChange}>
        {allowHistoricalEncounterUpload && showPaperOption && (
          <FormControlLabel
            value="paper"
            label={EncounterMode.PAPER}
            control={<Radio />}
          />
        )}
        {allowSimplifiedEncounter && showSimpleOption && (
          <FormControlLabel
            value="simple"
            label={EncounterMode.SIMPLE}
            control={<Radio />}
          />
        )}
        {allowFullEncounter && showFullOption && (
          <FormControlLabel
            disabled={isDetailedRestricted}
            value="full"
            label={EncounterMode.FULL}
            control={<Radio />}
          />
        )}
      </RadioGroup>
    </FormControl>
  );
};

export default EncounterModeSelector;
